/* eslint-disable react/no-unescaped-entities */
/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useFormik } from 'formik';
import { useIntl } from 'react-intl';
import ReactInputVerificationCode from 'react-input-verification-code';
import messages from './messages';
import AuthService from '../../../services/cognito/auth';
import PrimaryButton from '../../common/PrimaryButton';
import LinkButton from '../../common/LinkButton';
import ToastBanner from '../../common/ToastBanner';
import styles from './CreatePasswordForm.module.scss';
import InputPassword from '../../common/InputPassword';
import validator from './validator';
import { CUSTOMERCARE_PHONE } from '../../../utils/common/constants';
import {
  setShowLoginModal,
  setShowResetModalByDefault,
  setLoadResetPasswordComponent,
  setResetPasswordSuccess,
  setIsResendEmailSubmitted,
} from '../../../slices/accounts';

const CreatePasswordForm = ({
  customerEmail,
}) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const [resetPasswordResponse, setResetPasswordResponse] = useState(false);
  const [isResend, setIsResend] = useState(false);
  const [showToastBanner, setShowToastBanner] = useState(true);
  const [apiErrors, setAPIErrors] = useState({});
  const apiErrorCodes = {
    CodeMismatchException: formatMessage(messages.verificationCodeMismatchException),
    ExpiredCodeException: formatMessage(messages.verificationCodeExpiredCodeException),
    LimitExceeded: formatMessage(messages.verificationCodeResetLimitExceeded),
  };

  const resetPasswordSubmit = async ({ verificationCode, newPassword }) => {
    try {
      const response = await AuthService.resetPasswordSubmit(
        { username: customerEmail, code: verificationCode, password: newPassword },
      );
      if (response === 'SUCCESS') {
        setResetPasswordResponse(true);
      }
    } catch (error) {
      const errorMessageCode = Object.keys(apiErrorCodes).find((errorCode) =>
        error.name.includes(errorCode) ?? errorCode);
      setAPIErrors({
        verificationCodeError: apiErrorCodes[errorMessageCode] || error,
      });
    }
  };

  const resendVerificationEmail = async () => {
    try {
      const response = await AuthService.resendConfirmationCode({ username: customerEmail });
      if (response?.CodeDeliveryDetails?.Destination) {
        setIsResend(true);
      }
    } catch (error) {
      setAPIErrors({
        verificationCodeError: 'setLengthFormError',
      });
    }
  };

  const closeResentPasswordToastBanner = () => {
    setIsResend(false);
    setShowToastBanner(false);
  };

  const {
    values, errors, handleChange, handleBlur, handleSubmit, isValid, touched, setFieldValue, setTouched, submitCount,
  } =
    useFormik({
      initialValues: {
        newPassword: '',
        confirmPassword: '',
        verificationCode: null,
      },
      onSubmit: resetPasswordSubmit,
      validate: (v) => validator(v, { formatMessage }),
    });

  useEffect(() => {
    if (resetPasswordResponse) {
      dispatch(setShowResetModalByDefault({ showResetModalByDefault: false }));
      dispatch(setShowLoginModal({ showLoginModal: true }));
      dispatch(setLoadResetPasswordComponent({ loadResetPasswordComponent: false }));
      dispatch(setResetPasswordSuccess({ resetPasswordSuccess: true }));
      dispatch(setIsResendEmailSubmitted({ isResendEmailSubmitted: false }));
    }
  }, [dispatch, resetPasswordResponse]);

  return (
    <div className={styles.createPasswordForm}>
      {showToastBanner && (
        <ToastBanner
          type="warning"
          onClickCloseButton={closeResentPasswordToastBanner}
        >
          <div>
            {
              isResend ?
                `${formatMessage(messages.resentConfirmMessagePart1)} 
                ${customerEmail}. 
                ${formatMessage(messages.resentConfirmMessagePart2)}.`
                :
                `${formatMessage(messages.codeSentConfirmMessage)} ${customerEmail}.`
            }
          </div>
        </ToastBanner>
      )}
      <div className={styles['m-b-16']}>
        <form onSubmit={handleSubmit}>
          {customerEmail !== null && (
            <div className={styles['confirm-code']}>
              <ReactInputVerificationCode
                autoFocus
                name="verificationCode"
                id="verificationCode"
                value={values.verificationCode}
                length={6}
                onChange={(code) => {
                  setFieldValue('verificationCode', code);
                  setTouched({
                    ...touched,
                    verificationCode: true,
                  });
                }}
                placeholder=""
                className={errors.verificationCode ? 'verification-code--error' : ''}
              />
            </div>
          )}
          {
            (submitCount > 0 && (errors.verificationCodeError || apiErrors?.verificationCodeError)) &&
            (
              <div className={styles.form__error}>
                {errors.verificationCodeError || apiErrors?.verificationCodeError}
              </div>
            )
          }
          <div className={styles.resendButton}>
            <span>{formatMessage(messages.resendLinkText)}</span>
            <LinkButton
              id="resendButton"
              isBold
              onClick={resendVerificationEmail}
              linkText={formatMessage(messages.resendLinkButtonText)}
            />
          </div>
          <div className={classnames(styles['form-input'], styles['m-b-16'])}>
            <div className={styles['form-input']}>
              <InputPassword
                id="newPassword"
                name="newPassword"
                label="New Password"
                handleOnChange={handleChange}
                handleOnBlur={handleBlur}
                value={values.newPassword}
                isValid={isValid}
                errorMsg={touched.newPassword && errors.passwordError}
              />
            </div>
          </div>
          <div className={styles['form-input']}>
            <InputPassword
              id="confirmPassword"
              name="confirmPassword"
              label="Confirm Password"
              handleOnChange={handleChange}
              handleOnBlur={handleBlur}
              value={values.confirmPassword}
              isValid={isValid}
              errorMsg={touched.confirmPassword && errors.confirmPasswordError}
            />
          </div>
          <div className={styles['m-b-s']}>
            <PrimaryButton
              onClick={() => {}}
              title="Reset Password"
              type="submit"
              dataTestId="reset_password_button"
            />
          </div>
        </form>
        <p className={styles.bold}>
          {formatMessage(messages.customerCareHelp)}
        </p>
        <div className={classnames(
          styles['ff-graphik'],
          styles['fs-p3'],
          styles['lh-24'],
          styles['customer-care-link'],
        )}
        >
          {formatMessage(messages.customerCareHelpText)}
          <a
            className={classnames(
              styles['c-light-midnight'],
              styles['td-underline'],
              styles['td-none'],
              styles[' m-l-5'],
            )}
            href={`tel:${CUSTOMERCARE_PHONE}`}
          >
            {CUSTOMERCARE_PHONE}
          </a>
        </div>
      </div>
      <p className={styles['customer-care-link']}>
        {`${formatMessage(messages.customerCareHelpQuestions)} ${formatMessage(messages.customerCareHelpQuestionsText)} `}
        <a
          className={classnames(
            styles['td-underline'],
            styles['fs-p3'],
          )}
          href="https://purple.com/support"
          rel="noreferrer"
          target="_blank"
        >
          {formatMessage(messages.customerCareHelpFAQsText)}
        </a>
      </p>
    </div>
  );
};

CreatePasswordForm.propTypes = {
  customerEmail: PropTypes.string,
};
CreatePasswordForm.defaultProps = {
  customerEmail: null,
};
export default CreatePasswordForm;
